import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  FigureFilterItemType,
  FigureFilterType,
  FigureReqBodyType,
  FigureSettingsState,
  IFigureSettingItem,
  RepairItemStatusType,
} from "./types";
import $api from "@/shared/http";
import { RootState } from "@/app/appStore";
import { PageSizeType } from "@/app/model/types";
import { toApiError } from "@/shared/http/types";

interface FetchFiguresResponse {
  items: IFigureSettingItem[];
  totalCount: number;
}

interface FetchFiltersResponse {
  filters: FigureFilterType;
}

// Асинхронные действия
export const fetchFigures = createAsyncThunk<
  FetchFiguresResponse,
  void,
  { state: RootState; rejectValue: string }
>("figureSettings/fetchFigures", async (_, { getState, rejectWithValue }) => {
  const state = getState().figureSettings;
  const body: FigureReqBodyType = {
    order: state.sortRules,
    pagination: {
      pageSize: state.pageSize,
      pageNumber: state.pageNumber,
    },
    filters: [{ fields: state.selectedFilters, type: "eq" }],
  };

  try {
    const response = await $api.post<FetchFiguresResponse>(
      "/figure/list",
      body,
    );
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message || "Failed to fetch figures");
  }
});

export const fetchFilters = createAsyncThunk<
  FetchFiltersResponse,
  void,
  { rejectValue: string }
>("figureSettings/fetchFilters", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.post<FetchFiltersResponse>("/filters/figure", {
      filters: [],
    });
    return response.data;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message || "Failed to fetch filters");
  }
});

export const fetchAllFigures = createAsyncThunk<
  IFigureSettingItem[],
  void,
  { rejectValue: string }
>("figureSettings/fetchAllFigures", async (_, { rejectWithValue }) => {
  try {
    const response = await $api.post<FetchFiguresResponse>("/figure/list", {
      order: { type: "asc", field: "number" },
      pagination: { pageSize: 10000, pageNumber: 1 },
      filters: [],
    });
    return response.data.items;
  } catch (error) {
    const apiError = toApiError(error);
    return rejectWithValue(apiError.message || "Failed to fetch all figures");
  }
});

export const changeFigureStatus = createAsyncThunk<
  void,
  {
    figureSettings: {
      id: string;
      repairStatus: RepairItemStatusType;
    }[];
  },
  { rejectValue: string }
>("figureSettings/changeFigureStatus", async (data, { rejectWithValue }) => {
  try {
    await $api.put("/figure/change", data);
  } catch (error) {
    const apiError = toApiError(error);
    if (apiError.status === 403) {
      const details = (apiError.data as { detail?: string }).detail;
      return rejectWithValue(details || "Не удалось изменить статус");
    }
    return rejectWithValue(apiError.message || "Не удалось изменить статус");
  }
});

const initialState: FigureSettingsState = {
  list: [],
  isLoading: false,
  error: null,
  errorChange: null,
  allFigureIds: [],
  allFiguresList: [],
  sortRules: {
    type: "desc",
    field: "number",
  },
  totalCount: 0,
  selectedFigures: [],
  filters: [],
  selectedFilters: {},
  pageNumber: 1,
  pageSize: 20,
  isStatusChangeLoading: false,
};

// Слайс
const figureSettingsSlice = createSlice({
  name: "figureSettings",
  initialState,
  reducers: {
    setSortRules: (state, action: { payload: typeof state.sortRules }) => {
      state.sortRules = action.payload;
      state.pageNumber = 1;
    },
    setSelectedFilters: (state, action: { payload: FigureFilterItemType }) => {
      state.pageNumber = 1;
      state.selectedFilters = action.payload;
    },
    clearSelectedFilters: (state) => {
      state.selectedFilters = {};
      state.pageNumber = 1;
    },
    setSelectedFigures: (state, action: { payload: string[] }) => {
      state.selectedFigures = action.payload;
    },
    addSelectedFigure: (state, action: { payload: string }) => {
      if (!state.selectedFigures.includes(action.payload)) {
        state.selectedFigures.push(action.payload);
      }
    },
    removeSelectedFigure: (state, action: { payload: string }) => {
      state.selectedFigures = state.selectedFigures.filter(
        (id) => id !== action.payload,
      );
    },
    toggleSelectedFigure: (state, action: { payload: string }) => {
      const index = state.selectedFigures.indexOf(action.payload);
      if (index > -1) {
        state.selectedFigures.splice(index, 1);
      } else {
        state.selectedFigures.push(action.payload);
      }
    },
    selectAllFigures: (state, action: { payload: string[] }) => {
      state.selectedFigures = action.payload;
    },
    deselectAllFigures: (state) => {
      state.selectedFigures = [];
    },
    setPageSize: (state, action: { payload: PageSizeType }) => {
      state.pageSize = action.payload;
      state.pageNumber = 1;
    },
    setPageNumber: (state, action: { payload: number }) => {
      state.pageNumber = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchAllFigures
      .addCase(fetchAllFigures.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchAllFigures.fulfilled, (state, action) => {
        state.isLoading = false;
        state.allFiguresList = action.payload;
      })
      .addCase(fetchAllFigures.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          (action.payload as string) || "Failed to fetch all figures";
      })
      // fetchFigures
      .addCase(fetchFigures.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFigures.fulfilled, (state, action) => {
        state.selectedFigures = [];
        state.isLoading = false;
        state.list = action.payload.items;
        state.allFigureIds = action.payload.items?.map((p) => p.id);
        state.totalCount = action.payload.totalCount || 0;
      })
      .addCase(fetchFigures.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || "Failed to fetch figures";
      })
      // fetchFilters
      .addCase(fetchFilters.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFilters.fulfilled, (state, action) => {
        state.isLoading = false;
        state.filters = action.payload.filters;
      })
      .addCase(fetchFilters.rejected, (state, action) => {
        state.isLoading = false;
        state.error = (action.payload as string) || "Failed to fetch filters";
      })
      // changeFigureStatus
      .addCase(changeFigureStatus.pending, (state) => {
        state.isStatusChangeLoading = true;
        state.errorChange = null;
      })
      .addCase(changeFigureStatus.fulfilled, (state) => {
        state.isStatusChangeLoading = false;
      })
      .addCase(changeFigureStatus.rejected, (state, action) => {
        state.isStatusChangeLoading = false;
        state.errorChange =
          (action.payload as string) || "Не удалось изменить статус";
      });
  },
});

export const {
  setSortRules,
  setSelectedFilters,
  clearSelectedFilters,
  setSelectedFigures,
  addSelectedFigure,
  removeSelectedFigure,
  toggleSelectedFigure,
  selectAllFigures,
  deselectAllFigures,
  setPageSize,
  setPageNumber,
} = figureSettingsSlice.actions;
export default figureSettingsSlice;
